import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { SearchIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon } from '@heroicons/react/outline';
import BackToDashboardButton from '../components/common/BackToDashboardButton';
import Breadcrumbs from '../components/common/Breadcrumbs';
import RefundTerms from '../components/RefundTerms';

// Helper function to safely format currency amounts
const formatCurrency = (amount: any): string => {
  if (typeof amount === 'number') {
    return amount.toFixed(2);
  }
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount);
    return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
  }
  return '0.00';
};

// Types
interface OrderItem {
  id: number;
  listing_id: number;
  quantity: number;
  price_per_item: number;
  line_total?: number;
  listing_title?: string;
  listing_image_url?: string;
  listing_description?: string;
}

interface TransactionBreakdown {
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total: number;
  items_count: number;
}

interface Order {
  id: number;
  seller_id: number;
  listing_id?: number;
  price_at_purchase?: number;
  total_amount?: number;
  status: 'pending' | 'pending_sale' | 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancelled' | 'disputed' | 'refunded';
  created_at: string;
  shipped_at?: string;
  delivered_at?: string;
  shipping_status?: string;
  tracking_number?: string;
  listing_title?: string;
  image_url?: string;
  seller_username?: string;
  seller_business_name?: string;
  refund_status?: string | null;
  payment_completed_at?: string;
  refund_eligible_until?: string;
  shipping_info?: any;
  order_items?: OrderItem[];
  items?: Array<{
    listing_id: number;
    title: string;
    image_url: string | null;
    quantity: number;
    price: number;
  }>;
  transaction_breakdown?: TransactionBreakdown;
  subtotal?: number;
  tax_amount?: number;
  quantity?: number;
}

interface RefundEligibility {
  eligible: boolean;
  reason?: string;
  daysRemaining?: number;
}

type SortField = 'id' | 'created_at' | 'price_at_purchase' | 'status';
type SortDirection = 'asc' | 'desc';

interface OrderDetailsModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [refundEligibility, setRefundEligibility] = useState<RefundEligibility | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [showRefundRequestModal, setShowRefundRequestModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const isMountedRef = useRef(true);
  
  // Fetch detailed order information when modal opens
  useEffect(() => {
    if (isOpen && order) {
      const abortController = new AbortController();
      
      const fetchOrderDetails = async () => {
        try {
          setLoadingDetails(true);
          const token = localStorage.getItem('token');
          if (!token) return;
          
          const response = await axios.get(`/api/orders/${order.id}/details`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            signal: abortController.signal
          });
          
          // Only update state if component is still mounted and request wasn't cancelled
          if (isMountedRef.current && !abortController.signal.aborted) {
            if (response.data) {
              setDetailedOrder(response.data);
            }
          }
        } catch (error: any) {
          // Ignore cancellation errors
          if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            return;
          }
          // Log detailed error information
          console.error('Error fetching order details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            code: error.code,
            name: error.name
          });
          // Fallback to using the order prop if API fails, but only if still mounted
          if (isMountedRef.current) {
            setDetailedOrder(order);
          }
        } finally {
          if (isMountedRef.current) {
            setLoadingDetails(false);
          }
        }
      };
      
      fetchOrderDetails();
      
      // Cleanup: cancel the request if component unmounts or modal closes
      return () => {
        abortController.abort();
      };
    }
  }, [isOpen, order]);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const checkRefundEligibility = useCallback(async () => {
    if (!order) return;
    
    try {
      setCheckingEligibility(true);
      const token = localStorage.getItem('token');
      if (!token) {
        if (isMountedRef.current) {
          setRefundEligibility({
            eligible: false,
            reason: 'Authentication required'
          });
        }
        return;
      }
      
      const response = await axios.get(`/api/refunds/orders/${order.id}/eligibility`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (isMountedRef.current) {
        setRefundEligibility(response.data);
      }
    } catch (error: any) {
      // Ignore cancellation errors
      if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      
      // Log detailed error information
      console.error('Error checking refund eligibility:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });
      
      if (isMountedRef.current) {
        setRefundEligibility({
          eligible: false,
          reason: error.response?.data?.error || error.response?.data?.message || 'Unable to check refund eligibility'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setCheckingEligibility(false);
      }
    }
  }, [order]);

  // Check refund eligibility when modal opens
  useEffect(() => {
    if (isOpen && order) {
      checkRefundEligibility();
    }
  }, [isOpen, order, checkRefundEligibility]);

  // Use detailed order if available, otherwise fall back to order prop
  // This is safe to use after hooks since it's just a derived value
  const displayOrder = detailedOrder || order;
  
  // Early return after all hooks - check order and isOpen, and ensure displayOrder exists
  if (!isOpen || !order || !displayOrder) return null;
  
  // TypeScript type narrowing: after the check above, displayOrder is guaranteed to be non-null
  // Use type assertion to tell TypeScript that displayOrder is non-null after the check
  const safeDisplayOrder = displayOrder as Order;

  const handleRequestRefund = async () => {
    if (!order) {
      setStatusMessage({
        type: 'error',
        text: 'Order information is not available'
      });
      return;
    }

    if (!refundReason.trim()) {
      setStatusMessage({
        type: 'error',
        text: 'Please provide a reason for the refund request'
      });
      return;
    }

    try {
      setSubmittingRefund(true);
      setStatusMessage(null);

      const response = await axios.post('/api/refunds/request', {
        orderId: order.id,
        reason: refundReason
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setStatusMessage({
          type: 'success',
          text: 'Refund request submitted successfully! You will be notified once it is reviewed.'
        });
        setShowRefundRequestModal(false);
        setRefundReason('');
        // Refresh eligibility
        setTimeout(() => {
          checkRefundEligibility();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error requesting refund:', error);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.message || 'Failed to submit refund request'
      });
    } finally {
      if (isMountedRef.current) {
        setSubmittingRefund(false);
      }
    }
  };
  
  if (!isOpen || !order) return null;
  


  // Function to cancel order
  const handleCancelOrder = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      // Confirm cancellation
      if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
        return;
      }
      
      await axios.put(`/api/orders/${order.id}/status`, {
        status: 'cancelled'
      });
      
      setStatusMessage({
        type: 'success',
        text: 'Order cancelled successfully!'
      });
      
      // Close modal after successful cancellation
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      
      // Check if the error has a response with data
      if (error.response && error.response.data) {
        // If we have a specific error message from the server, display it
        setStatusMessage({
          type: 'error',
          text: error.response.data.message || 'Failed to cancel order. Please try again.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Failed to cancel order. Please try again.'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pt-2 pb-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Order #{safeDisplayOrder.id}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-800">Order Details</h3>
            <div className="bg-gray-50 p-4 rounded">
              <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Date:</span> <span className="text-gray-700">{new Date(safeDisplayOrder.created_at).toLocaleString()}</span></p>
              <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Status:</span> <span className="text-gray-700">{safeDisplayOrder.status}</span></p>
              <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Amount:</span> <span className="text-gray-700">${formatCurrency(safeDisplayOrder.total_amount || safeDisplayOrder.price_at_purchase)}</span></p>
              {safeDisplayOrder.tracking_number && (
                <div className="mb-2 text-gray-900">
                  <span className="font-medium text-gray-800">Tracking Number:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="bg-gray-200 px-2 py-1 rounded text-sm font-mono text-gray-900">{safeDisplayOrder.tracking_number}</code>
                    <button
                      onClick={() => {
                        const trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${safeDisplayOrder.tracking_number}`;
                        window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Track on USPS
                    </button>
                  </div>
                </div>
              )}
              {safeDisplayOrder.shipping_status && (
                <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Shipping Status:</span> <span className="text-gray-700">{safeDisplayOrder.shipping_status}</span></p>
              )}
              {safeDisplayOrder.shipped_at && (
                <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Shipped Date:</span> <span className="text-gray-700">{new Date(safeDisplayOrder.shipped_at).toLocaleString()}</span></p>
              )}
              {safeDisplayOrder.delivered_at && (
                <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Delivered Date:</span> <span className="text-gray-700">{new Date(safeDisplayOrder.delivered_at).toLocaleString()}</span></p>
              )}
              {/* Display shipping service if available */}
              {safeDisplayOrder.shipping_info && (() => {
                try {
                  const shippingInfo = typeof safeDisplayOrder.shipping_info === 'string' 
                    ? JSON.parse(safeDisplayOrder.shipping_info) 
                    : safeDisplayOrder.shipping_info;
                  if (shippingInfo.service || shippingInfo.carrier) {
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="mb-1 text-gray-900"><span className="font-medium text-gray-800">Shipping Service:</span></p>
                        <p className="text-sm text-gray-700">
                          {shippingInfo.carrier && <span className="font-medium text-gray-800">{shippingInfo.carrier}</span>}
                          {shippingInfo.service && (
                            <span className="ml-1 text-gray-700">
                              {shippingInfo.service === 'First Class Mail (PWE)' ? '📮 PWE' : shippingInfo.service}
                            </span>
                          )}
                        </p>
                        {shippingInfo.shipping_cost && (
                          <p className="text-sm text-gray-700 mt-1">
                            Cost: ${parseFloat(shippingInfo.shipping_cost).toFixed(2)}
                          </p>
                        )}
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-800">Seller Information</h3>
            <div className="bg-gray-50 p-4 rounded">
              <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Username:</span> <span className="text-gray-700">{safeDisplayOrder.seller_username || 'Unknown'}</span></p>
              <p className="mb-2 text-gray-900"><span className="font-medium text-gray-800">Seller ID:</span> <span className="text-gray-700">{safeDisplayOrder.seller_id}</span></p>
            </div>
          </div>
        </div>
        
        {/* Loading state for order details */}
        {loadingDetails && (
          <div className="mb-6 text-center py-4">
            <p className="text-gray-600">Loading order details...</p>
          </div>
        )}
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2 text-gray-800">Order Items</h3>
          <div className="bg-gray-50 p-4 rounded space-y-3">
            {/* Display order items if available from order details API */}
            {safeDisplayOrder.order_items && safeDisplayOrder.order_items.length > 0 ? (
              <>
                {safeDisplayOrder.order_items.map((item: any, index: number) => (
                  <div key={item.id || index} className="flex items-center border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                    {item.listing_image_url && (
                      <img 
                        src={item.listing_image_url} 
                        alt={item.listing_title || 'Item'} 
                        className="w-16 h-16 object-cover mr-4 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/placeholder.jpg';
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.listing_title || 'Item'}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity} × ${formatCurrency(item.price_per_item)} = ${formatCurrency(item.line_total || (item.price_per_item * item.quantity))}</p>
                    </div>
                  </div>
                ))}
                
                {/* Transaction Breakdown - only available from detailed API */}
                {safeDisplayOrder.transaction_breakdown && (
                  <div className="mt-4 pt-4 border-t-2 border-gray-300 space-y-2">
                    <h4 className="font-semibold text-gray-900 mb-2">Transaction Breakdown</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal ({safeDisplayOrder.transaction_breakdown.items_count} item{safeDisplayOrder.transaction_breakdown.items_count !== 1 ? 's' : ''}):</span>
                      <span className="font-medium text-gray-900">${formatCurrency(safeDisplayOrder.transaction_breakdown.subtotal)}</span>
                    </div>
                    {safeDisplayOrder.transaction_breakdown.shipping_cost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Shipping:</span>
                        <span className="font-medium text-gray-900">${formatCurrency(safeDisplayOrder.transaction_breakdown.shipping_cost)}</span>
                      </div>
                    )}
                    {safeDisplayOrder.transaction_breakdown.tax_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Tax:</span>
                        <span className="font-medium text-gray-900">${formatCurrency(safeDisplayOrder.transaction_breakdown.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-300">
                      <span className="text-gray-900">Total Paid:</span>
                      <span className="text-gray-900">${formatCurrency(safeDisplayOrder.transaction_breakdown.total)}</span>
                    </div>
                  </div>
                )}
              </>
            ) : safeDisplayOrder.items && safeDisplayOrder.items.length > 0 ? (
              /* Fallback to items array from order list (used before detailed API loads) */
              <>
                {safeDisplayOrder.items.map((item: any, index: number) => (
                  <div key={item.listing_id || index} className="flex items-center border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.title || 'Item'} 
                        className="w-16 h-16 object-cover mr-4 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/placeholder.jpg';
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.title || 'Item'}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity} × ${formatCurrency(item.price)} = ${formatCurrency(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-700 mt-2 italic">Loading full order details...</p>
              </>
            ) : (
              // Fallback to single item display if neither order_items nor items available
              <div className="flex items-center">
                {safeDisplayOrder.image_url && (
                  <img 
                    src={safeDisplayOrder.image_url} 
                    alt={safeDisplayOrder.listing_title || 'Item'} 
                    className="w-20 h-20 object-cover mr-4 rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/placeholder.jpg';
                    }}
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{safeDisplayOrder.listing_title || 'Item'}</p>
                  {safeDisplayOrder.listing_id && (
                    <p className="text-sm text-gray-600">Listing ID: {safeDisplayOrder.listing_id}</p>
                  )}
                  <p className="text-sm text-gray-600">Price: ${formatCurrency(safeDisplayOrder.total_amount || safeDisplayOrder.price_at_purchase)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Message Display */}
        {statusMessage && (
          <div className={`mb-4 p-3 rounded-md ${
            statusMessage.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : statusMessage.type === 'error'
                ? 'bg-red-100 text-red-800 border border-red-200'
                : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
            {statusMessage.text}
          </div>
        )}
        
        {/* Refund Eligibility Status */}
        {safeDisplayOrder.status !== 'refunded' && safeDisplayOrder.status !== 'cancelled' && (
          <div className="mb-4 border-t pt-4">
            {checkingEligibility ? (
              <div className="text-sm text-gray-600">Checking refund eligibility...</div>
            ) : refundEligibility ? (
              <div>
                {refundEligibility.eligible ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-800">Refund Eligible</p>
                        {refundEligibility.daysRemaining !== undefined && (
                          <p className="text-xs text-green-600 mt-1">
                            {refundEligibility.daysRemaining > 0 
                              ? `${refundEligibility.daysRemaining} day(s) remaining to request refund`
                              : 'Last day to request refund'}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setShowRefundRequestModal(true)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Request Refund
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800">Refund Not Available</p>
                    <p className="text-xs text-red-600 mt-1">{refundEligibility.reason}</p>
                  </div>
                )}
                <RefundTerms className="mt-3" />
              </div>
            ) : null}
          </div>
        )}

        {/* Refund Request Modal */}
        {showRefundRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Request Refund</h3>
              <RefundTerms className="mb-4" />
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Refund <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Please explain why you need a refund..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  rows={4}
                  required
                />
              </div>
              {statusMessage && (
                <div className={`mb-4 p-3 rounded-md ${
                  statusMessage.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {statusMessage.text}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRefundRequestModal(false);
                    setRefundReason('');
                    setStatusMessage(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={submittingRefund}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestRefund}
                  disabled={submittingRefund || !refundReason.trim()}
                  className={`px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 ${
                    submittingRefund || !refundReason.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {submittingRefund ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 border-t pt-4">
          {/* Cancel Order Button - Available for pending and processing orders */}
          {(order.status === 'pending' || order.status === 'pending_sale' || order.status === 'processing') && (
            <button
              onClick={handleCancelOrder}
              disabled={isProcessing}
              className={`bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? 'Processing...' : 'Cancel Order'}
            </button>
          )}
          
          {(safeDisplayOrder.status === 'shipped' || safeDisplayOrder.status === 'delivered') && (
            <button
              onClick={async () => {
                try {
                  setIsProcessing(true);
                  const response = await axios.post(`/api/orders/${safeDisplayOrder.id}/confirm-delivery`);
                  if (response.data.success) {
                    setStatusMessage({
                      type: 'success',
                      text: 'Delivery confirmed! Seller will be paid automatically.'
                    });
                    // Refresh the order data
                    window.location.reload();
                  }
                } catch (error: any) {
                  console.error('Error confirming delivery:', error);
                  setStatusMessage({
                    type: 'error',
                    text: error.response?.data?.error || 'Failed to confirm delivery'
                  });
                } finally {
                  setIsProcessing(false);
                }
              }}
              disabled={isProcessing}
              className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? 'Processing...' : 'Confirm Receipt'}
            </button>
          )}
          
          {['pending', 'shipped'].includes(safeDisplayOrder.status) && (
            <button
              onClick={() => {
                // Open USPS tracking page in new window
                if (safeDisplayOrder.tracking_number) {
                  const trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${safeDisplayOrder.tracking_number}`;
                  window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                } else {
                  alert("No tracking information available.");
                }
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"
              disabled={!safeDisplayOrder.tracking_number}
            >
              Track Shipment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const BuyerOrdersPage: React.FC = () => {
  useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch buyer orders
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/orders/my-orders', {
          signal: abortController.signal
        });
        
        // Only update state if component is still mounted and request wasn't cancelled
        if (!isMounted || abortController.signal.aborted) return;
        
        // Handle the backend response format which returns { orders: [...], pagination: {...} }
        if (response.data && response.data.orders && Array.isArray(response.data.orders)) {
          // Transform the data to ensure numeric values are properly formatted
          const processedOrders = response.data.orders.map((order: any) => ({
            ...order,
            // Map backend field names to frontend expected names
            price_at_purchase: typeof order.price_at_purchase === 'string' 
              ? parseFloat(order.price_at_purchase) 
              : Number(order.price_at_purchase),
            listing_title: order.listing_title || order.title || 'Unknown Item',
            image_url: order.image_url,
            seller_username: order.seller_username || 'Unknown Seller',
            seller_business_name: order.seller_business_name
          }));
          setOrders(processedOrders);
        } else {
          console.error('Unexpected response format:', response.data);
          setOrders([]);
        }
      } catch (err: any) {
        // Ignore cancellation errors
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED' || !isMounted) {
          return;
        }
        console.error('Error fetching buyer orders:', err);
        setError('Failed to load your orders. Please try again.');
      } finally {
        if (isMounted && !abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();
    
    // Cleanup function to prevent state updates on unmounted component and cancel requests
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  // Filter and sort orders
  const filteredAndSortedOrders = [...orders]
    .filter(order => {
      // Handle status filtering with new status values
      let statusMatches = selectedStatus === 'All';
      if (selectedStatus === 'Pending') {
        statusMatches = order.status === 'pending' || order.status === 'pending_sale';
      } else if (selectedStatus === 'Processing') {
        statusMatches = order.status === 'processing';
      } else if (selectedStatus === 'Shipped') {
        statusMatches = order.status === 'shipped';
      } else if (selectedStatus === 'Delivered') {
        statusMatches = order.status === 'delivered';
      } else if (selectedStatus === 'Completed') {
        statusMatches = order.status === 'completed';
      } else if (selectedStatus === 'Cancelled') {
        statusMatches = order.status === 'cancelled';
      }

      return statusMatches &&
        (order.id.toString().includes(searchTerm.toLowerCase()) || 
        (order.listing_title && order.listing_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.seller_username && order.seller_username.toLowerCase().includes(searchTerm.toLowerCase())));
    })
         .sort((a, b) => {
       const aValue = a[sortField];
       const bValue = b[sortField];
       
       // Handle undefined values for sorting
       if (aValue === undefined && bValue === undefined) return 0;
       if (aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
       if (bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
       
       if (sortDirection === 'asc') {
         return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
       } else {
         return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
       }
     });

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (order: Order) => {
    setViewingOrder(order);
    setIsModalOpen(true);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 inline ml-1" />
      : <ArrowDownIcon className="h-4 w-4 inline ml-1" />;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-indigo-100 text-indigo-800';
      case 'processing':
        return 'bg-orange-100 text-orange-800';
      case 'pending_sale':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'disputed':
        return 'bg-gray-100 text-gray-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <Breadcrumbs 
            items={[
              { label: 'Home', path: '/' },
              { label: 'My Orders', isCurrentPage: true }
            ]}
            className="mb-4"
          />
        </div>
        
        <div className="mb-6">
          <BackToDashboardButton customReturnPath="/profile" buttonText="Back to Dashboard" />
        </div>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Purchases</h1>
          <p className="text-gray-600 mt-1">View and track your orders</p>
        </div>
        
        <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full md:w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
            <div className="flex flex-wrap gap-2">
              {['All', 'Pending', 'Processing', 'Shipped', 'Delivered', 'Completed', 'Cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`px-3 py-1 text-sm rounded-full ${
                    selectedStatus === status
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {filteredAndSortedOrders.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-600">No orders found.</p>
            {orders.length > 0 ? (
              <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
            ) : (
              <p className="mt-2 text-sm text-gray-500">You haven't made any purchases yet.</p>
            )}
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('id')}
                    >
                      Order ID {renderSortIcon('id')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Seller
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Item
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('price_at_purchase')}
                    >
                      Amount {renderSortIcon('price_at_purchase')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status {renderSortIcon('status')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('created_at')}
                    >
                      Order Date {renderSortIcon('created_at')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.seller_username || `Seller #${order.seller_id}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.items && order.items.length > 1 ? (
                          <>
                            <div className="text-sm text-gray-900">{order.items.length} items</div>
                            <div className="text-xs text-gray-500">{order.items[0]?.title?.substring(0, 25)}{order.items[0]?.title?.length > 25 ? '...' : ''} + more</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-gray-900">{order.listing_title}</div>
                            <div className="text-xs text-gray-500">ID: {order.listing_id}</div>
                          </>
                        )}
                      </td>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                         ${formatCurrency(order.total_amount || order.price_at_purchase)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewDetails(order)}
                          className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      <OrderDetailsModal 
        order={viewingOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default BuyerOrdersPage; 